import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('clm_user')); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem('clm_token');
    if (!token) { setLoading(false); return; }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      localStorage.setItem('clm_user', JSON.stringify(res.data));
    } catch {
      localStorage.removeItem('clm_token');
      localStorage.removeItem('clm_user');
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('clm_token', access_token);
    localStorage.setItem('clm_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role) => {
    const res = await api.post('/auth/register', { email, password, name, role });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('clm_token', access_token);
    localStorage.setItem('clm_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const trialSignup = async ({ name, email, password, company }) => {
    const res = await api.post('/auth/trial-signup', { name, email, password, company });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('clm_token', access_token);
    localStorage.setItem('clm_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const loginWithSession = (userData, token) => {
    localStorage.setItem('clm_token', token);
    localStorage.setItem('clm_user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('clm_token');
    localStorage.removeItem('clm_user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, trialSignup, loginWithSession, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
